import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import toast from 'react-hot-toast';
import { useRedirectIfAuthenticated } from '../../hooks/useRedirectIfAuthenticated';
import BgLogin from '../../images/BgLogin.png';

const CheckIcon = () => (
  <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
  </svg>
);

const LoginPage = () => {
  useRedirectIfAuthenticated();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login({ email, password });
      toast.success('Login successful!');

      const role = result?.data?.user?.role;
      const defaultPath =
        role === 'resident' ? '/wallet' : role === 'national_official' ? '/ayuda' : '/dashboard';

      setTimeout(() => {
        window.location.replace(defaultPath);
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Login failed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${BgLogin})` }}
    >
      <div className="absolute inset-0 bg-black/40"></div>
      <div className="z-10 w-full max-w-5xl bg-white rounded-[2rem] shadow-2xl flex flex-col md:flex-row overflow-hidden min-h-[600px]">
        <div className="hidden md:flex flex-col justify-center w-[45%] bg-[#f0f7ff] p-12">
          <div className="flex flex-col items-center text-center">
            <div className="mb-6 p-4 rounded-full bg-white shadow-sm">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.5">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-2.5 3.6-4 8-4s8 1.5 8 4" />
              </svg>
            </div>

            <h2 className="text-4xl font-extrabold text-[#1e40af] mb-4">Welcome Back!</h2>
            <p className="text-gray-600 text-base mb-10 leading-relaxed">
              Sign in to your Barangay 360 account and manage your community with ease.
            </p>

            <div className="w-full space-y-4">
              {[
                "Secure, fast login",
                "Access resident & official features",
                "Modern, mobile-friendly design"
              ].map((text, i) => (
                <div key={i} className="flex items-center space-x-3 bg-[#e0eeff] p-4 rounded-xl">
                  <CheckIcon />
                  <span className="text-[#1e40af] font-semibold text-sm">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full md:w-[55%] p-8 md:p-16 flex flex-col justify-center">
          <div className="flex flex-col items-center mb-10">
            <div className="h-20 w-20 rounded-2xl bg-[#7c5cfc] flex items-center justify-center shadow-lg mb-4">
              <span className="text-white font-bold text-2xl">B360</span>
            </div>
            <h1 className="text-3xl font-bold text-[#1e293b]">Barangay 360</h1>
            <p className="text-gray-400 text-sm font-medium uppercase tracking-widest">Management System</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-[#1e293b] mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-5 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 focus:ring-2 focus:ring-[#2563eb] outline-none transition-all"
                placeholder="Enter address"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-[#1e293b] mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-5 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 focus:ring-2 focus:ring-[#2563eb] outline-none transition-all"
                placeholder="Enter password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all shadow-lg ${isLoading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#2563eb] text-white hover:bg-[#1d4ed8] active:scale-[0.98]'
                }`}
            >
              {isLoading ? 'Signing In...' : 'Login'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-500 font-medium">
              Don't have an account yet?{' '}
              <Link to="/register" className="text-[#2563eb] font-bold hover:underline">
                Sign Up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;